"use client";

import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { EmptyState } from "@/components/ui/EmptyState";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { getBuySellRowId, type BuySellProduct } from "@/model/services/buysellapi";
import { VehicleCard } from "./VehicleCard";
import { BuySellErrorState } from "./ErrorState";
import { VehicleGridSkeleton } from "./LoadingSkeleton";
import { Typography } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

type FeaturedVehiclesGridProps = {
  products: BuySellProduct[];
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  onViewDetails: (productId: string) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  browseAllHref?: string;
  onBrowseAll?: () => void;
};

export function FeaturedVehiclesGrid({
  products,
  loading = false,
  error = "",
  onRetry,
  onViewDetails,
  emptyTitle = "No featured vehicles",
  emptyDescription = "There are no active featured listings right now. Check back soon or browse all vehicles.",
  onBrowseAll,
}: FeaturedVehiclesGridProps) {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const itemsPerSlide = isMobile ? 1 : isTablet ? 2 : 3;

  const autoplay = useMemo(() =>
    Autoplay({ delay: 3000, stopOnMouseEnter: true, stopOnInteraction: false, }),
    [],);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start", loop: products.length > itemsPerSlide, slidesToScroll: 1,
    }, [autoplay],);

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const updateSelectedIndex = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on("select", updateSelectedIndex); updateSelectedIndex();
    return () => {
      emblaApi.off("select", updateSelectedIndex);
    };
  }, [emblaApi]);

  if (loading) {
    return <VehicleGridSkeleton count={4} />;
  }

  if (error) {
    return (
      <BuySellErrorState
        message={error}
        onRetry={onRetry ? () => void onRetry() : undefined}
      />
    );
  }

  if (products.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        icon={<SearchOffOutlinedIcon sx={{ fontSize: 36 }} />}
        action={
          onBrowseAll ? (
            <Button variant="contained" onClick={onBrowseAll}>
              Browse all vehicles
            </Button>
          ) : undefined
        }
      />
    );
  }

  const canNavigate = products.length > itemsPerSlide;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
          color: "text.secondary"
        }}
      >
        <Typography variant="caption"> Auto-sliding featured vehicles </Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
      </Box>
      <Box sx={{ position: "relative", px: { xs: 0, sm: 2 } }}>
        {/* Previous slide */}
        {canNavigate && (
          <IconButton
            aria-label="Previous vehicles"
            onClick={() => emblaApi?.scrollPrev()}
            sx={{
              position: "absolute",
              left: { xs: 0, sm: -15 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 40,
              height: 40,
              borderRadius: "50%",
              color: "#000",
              backgroundColor: "transparent !important",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              boxShadow: "none",
              backdropFilter: "blur(4px)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.75)",
                boxShadow: "none",
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
              },
            }} >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        {/* Carousel viewport */}
        <Box
          ref={emblaRef}
          sx={{ overflow: "hidden", width: "100%", }}
        >
          <Box
            sx={{ display: "flex", touchAction: "pan-y pinch-zoom", ml: -1.25, }}
          >
            {products.map((product) => (
              <Box
                key={getBuySellRowId(product)}
                sx={{
                  flex: `0 0 ${100 / itemsPerSlide}%`,
                  minWidth: 0,
                  boxSizing: "border-box",
                  px: 1.25,
                  py: 1,
                }}
              >
                <VehicleCard
                  product={product}
                  onClick={onViewDetails}
                  showViewAction
                  viewLabel="View Listing"
                  badge={{ label: "Featured", color: "#f97316" }}
                />
              </Box>
            ))}
          </Box>
        </Box>
        {/* Next slide */}
        {canNavigate && (
          <IconButton
            aria-label="Next vehicles"
            onClick={() => emblaApi?.scrollNext()}
            sx={{
              position: "absolute",
              right: { xs: 0, sm: -8 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 40,
              height: 40,
              borderRadius: "50%",
              color: "#000",
              backgroundColor: "transparent !important",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              boxShadow: "none",
              backdropFilter: "blur(4px)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.75)",
                boxShadow: "none",
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
              },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>)}
      </Box>
      {/* Slide indicators */}
      {canNavigate && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            mt: 2,
          }}
        >
          {Array.from({ length: emblaApi?.scrollSnapList().length ?? 0 }, (_, index) => (
            <Box
              key={index}
              component="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => emblaApi?.scrollTo(index)}
              sx={{
                width: 8,
                height: 8,
                minWidth: 8,
                border: 0,
                padding: 0,
                borderRadius: "50%",
                cursor: "pointer",
                bgcolor: selectedIndex === index ? "primary.main" : "action.disabledBackground",
                transition: "background-color 0.2s",
              }}
            />
          ),
          )}
        </Box>
      )}
    </Box>
  );
}
